import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import { CheckCircle, ArrowRight } from 'lucide-react';

const PreCheckout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('plan');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const planNames = {
    'plan_DBXVLNLTqBIYK': 'Pro Monthly - $47/month',
    'plan_2nQAUoWggHLBk': 'Pro Annual - $470/year',
    'plan_eXbZqIxnR13uz': 'Elite Monthly - $97/month',
    'plan_auyU5uFXjcyIE': 'Elite Annual - $970/year'
  };

  const handleContinueToCheckout = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!email) {
        throw new Error('Please enter your email');
      }

      // Check if user already exists
      const { data: existingProfile } = await supabase
        .from('user_profiles')
        .select('id, whop_user_id')
        .eq('email', email)
        .single();

      if (existingProfile) {
        console.log('Existing user found, proceeding to checkout');
      } else {
        // Create a pending profile - will be activated when webhook fires
        console.log('Creating pending profile for new user');
        const { error: insertError } = await supabase
          .from('user_profiles')
          .insert({
            email: email,
            subscription_status: 'pending',
            onboarding_completed: false,
            skill_tier: 'beginner',
            xp_points: 0,
            level: 1,
            streak_days: 0,
          });

        if (insertError && !insertError.message.includes('duplicate')) {
          throw insertError;
        }
      }

      // Store email in session for post-purchase
      sessionStorage.setItem('checkout_email', email);

      // Redirect to Whop checkout
      const checkoutUrl = `https://whop.com/checkout/${planId}?d2c=true`;
      window.location.href = checkoutUrl;

    } catch (err) {
      console.error('Pre-checkout error:', err);
      setError(err.message || 'Something went wrong');
      setLoading(false);
    }
  };

  if (!planId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Invalid plan selected</p>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-card rounded-2xl shadow-xl p-8 border border-border">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Almost There!</h1>
            <p className="text-muted-foreground text-sm">
              Enter your email to continue to checkout
            </p>
          </div>

          <div className="bg-muted/30 rounded-lg p-4 mb-6 border border-border">
            <p className="text-sm text-muted-foreground mb-1">Selected Plan:</p>
            <p className="font-semibold">{planNames[planId] || 'Premium Plan'}</p>
          </div>

          <form onSubmit={handleContinueToCheckout} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                required
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground mt-2">
                We'll send your login link here after purchase
              </p>
            </div>

            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  Continue to Checkout
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate(-1)}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Go Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreCheckout;
